import { Button, Chip, IconButton, Icons, Toggler } from 'plume-ui';
import { AdjustableComponent } from 'plume-ui/dist/hooks/useClasses';
import { memo, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useRecoilValue } from 'recoil';
import CsvManagementModal from '../../../../../components/CsvManagement/CsvManagementModal';
import { DependencyContainer } from '../../../../../DependencyContainer';
import { useFetchData } from '../../../../../hooks/useFetchData';
import { activePartnerAtom } from '../../../../../state';
import { CsvFileSummary, Maybe } from '../../../../../types';
import FormattedMessage from '../../../../../utils/components/FormattedMessage';
import { incExcTogglerData, IncludeExclude } from '../fixtures';

type IncludeExcludeListProps = {
  selectedValue?: string;
  initialType?: IncludeExclude;
  onDelete?: () => void;
  onChange?: (data: CsvFileSummary) => void;
};

type IncludeExcludeListStyles = {
  root: string;
};
const { csvLocationsFileService } = new DependencyContainer();

const IncludeExcludeList: AdjustableComponent<
  IncludeExcludeListProps,
  IncludeExcludeListStyles
> = ({ selectedValue, initialType, onDelete, onChange }) => {
  const { t } = useTranslation();

  const activePartner = useRecoilValue(activePartnerAtom);

  const [showModal, setShowModal] = useState(false);
  const [incExcList, setIncExcList] = useState<Maybe<CsvFileSummary>>();
  const [listType, setListType] = useState<IncludeExclude>(
    initialType ?? 'include',
  );

  const [, csvTable] = useFetchData<CsvFileSummary[], any>(
    async () => {
      if (!activePartner?.id) return;
      return await csvLocationsFileService.getCsvLocationsFileList(
        activePartner.id,
      );
    },
    undefined,
    [activePartner],
    () => activePartner === undefined,
  );

  useEffect(() => {
    if (csvTable === undefined || selectedValue === undefined) return;
    setIncExcList(csvTable?.find((itm) => itm.file_id === selectedValue));
  }, [csvTable, selectedValue]);

  useEffect(() => {
    if (incExcList === undefined) return;
    onChange?.({ ...incExcList, criteriaType: listType });
  }, [incExcList, listType]);

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <>
      <div className="FilterItem FilterConfig__section">
        <div className="FilterItem__column FilterItem__deleteButton">
          <IconButton onClick={onDelete}>
            <Icons.CrossIcon />
          </IconButton>
        </div>
        <div className="FilterItem__column" data-testid="inc-exc-toggler">
          <Toggler
            classes={(current) => ({
              ...current,
              toggle: `${current.toggle}`,
              root: `${current.root}`,
            })}
            value={listType}
            toggleElements={incExcTogglerData}
            onToggle={(el) => {
              setListType(el.key as IncludeExclude);
            }}
            variant="large"
          />
        </div>

        <div className="FilterItem__column">
          <Button
            styleVariant="secondary"
            icon={<Icons.UploadIcon />}
            onClick={() => {
              setShowModal(true);
            }}
          >
            <FormattedMessage id="startUpload" />
          </Button>
        </div>

        <div className="FilterItem__column">
          {incExcList !== undefined && (
            <Chip
              type={'default'}
              removable
              onClick={() => setIncExcList(undefined)}
            >
              {incExcList.name}
            </Chip>
          )}
        </div>
      </div>
      <CsvManagementModal
        title={
          listType === 'include'
            ? t('monitorAdd.uploadCsvModal.includeTitle')
            : t('monitorAdd.uploadCsvModal.excludeTitle')
        }
        show={showModal}
        callback={(dat) => {
          setIncExcList({
            ...dat,
            criteriaType: listType,
          });
        }}
        onClose={handleModalClose}
      />
    </>
  );
};

export default memo(IncludeExcludeList);
